import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CarService } from 'src/app/services/car/car.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';
import { CarModelService } from 'src/app/services/car-model/car-model.service';
import { UpdateCarModalService } from 'src/app/services/update-car-modal/update-car-modal.service';

declare var KTMenu: any;
declare var bootstrap: any;

@Component({
  selector: 'app-vh-aucnet-data',
  templateUrl: './vh-aucnet-data.component.html',
  styleUrls: ['./vh-aucnet-data.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class VhAucnetDataComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  vehicles: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;
  addingToStockIds: Set<string> = new Set(); // Track which vehicles are being added to stock

  // Filter properties
  filters = {
    search: '',
    make: '',
    model: '',
    year: '',
    year_from: '',
    year_to: '',
    mileage_from: '',
    mileage_to: '',
    engine_size: '',
    minimumPrice: '',
    maximumPrice: '',
    type : 'aucnet'
  };

  // Dropdown data
  makesList: any[] = [];
  modelsList: any[] = [];
  fuelTypesList: any[] = [
    { value: 'petrol', label: 'Petrol' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'electric', label: 'Electric' },
    { value: 'lpg', label: 'LPG' }
  ];
  transmissionList: any[] = [
    { value: 'manual', label: 'Manual' },
    { value: 'automatic', label: 'Automatic' },
    { value: 'cvt', label: 'CVT' }
  ];

  constructor(
    private _carService: CarService,
    private _generalService: GeneralService,
    private _vehicleService: VehicleService,
    private _modelService: CarModelService,
    private _updateCarModalService: UpdateCarModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVehicles(1, '', this.filters);
    this.loadMakes();
  }

  ngAfterViewInit(): void {
    this.refreshMenus();
  }

  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  loadVehicles(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    this.subscriptions.push(
      this._carService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.vehicles = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.vehicles = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading vehicles:', error);
          this.vehicles = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadVehicles(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    console.log('Applied filters:', this.filters);
    this.loadVehicles(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      make: '',
      model: '',
      year: '',
      year_from: '',
      year_to: '',
      mileage_from: '',
      mileage_to: '',
      engine_size: '',
      minimumPrice: '',
      maximumPrice: '',
      type: 'aucnet'
    };
    this.searchTerm = '';
    this.modelsList = [];
    this.loadVehicles(1, '', {});
  }

  loadMakes(): void {
    this.subscriptions.push(
      this._generalService.GetMakes().subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.makesList = response.data.map((make: any) => ({
              value: make.slug || make._id,
              label: make.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading makes:', error);
        }
      })
    );
  }

  onMakeChange(makeSlug: string): void {
    this.filters.model = '';
    this.modelsList = [];
    if (makeSlug) {
      this.loadModels(makeSlug);
    }
  }

  onMakeSelectionChange(makeSlug: string | null): void {
    this.filters.make = makeSlug || '';
    this.onMakeChange(makeSlug || '');
  }

  loadModels(makeSlug: string): void {
    if (!makeSlug) {
      this.modelsList = [];
      return;
    }

    this.subscriptions.push(
      this._modelService.GetData("1", "", { make: makeSlug, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.modelsList = response.data.map((model: any) => ({
              value: model.slug || model._id,
              label: model.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading models:', error);
          this.modelsList = [];
        }
      })
    );
  }

  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  formatPrice(price: number): string {
    if (!price) return '-';
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0
    }).format(price);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  getVehicleImageUrl(vehicle: any): string | null {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return null;
  }

  hasVehicleImage(vehicle: any): boolean {
    return vehicle.pictures && vehicle.pictures.length > 0;
  }

  addToRealStock(vehicle: any): void {
    // Show confirmation dialog
    Swal.fire({
      title: 'Add to Real Stock',
      text: `Are you sure you want to add "${vehicle.name || 'this vehicle'}" to real stock?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, add it!',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-light'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.performAddToStock(vehicle);
      }
    });
  }

  viewVehicle(vehicle: any): void {
    window.open('/bid-manager/search-bids/view-bid/' + vehicle._id, '_blank');
  }

  private performAddToStock(vehicle: any): void {
    const vehicleId = vehicle._id;

    // Add to loading set
    this.addingToStockIds.add(vehicleId);

    this.subscriptions.push(
      this._vehicleService.AddToStock(vehicleId).subscribe({
        next: (response: any) => {
          // Remove from loading set
          this.addingToStockIds.delete(vehicleId);

          if (response.status) {
            // Show success message
            Swal.fire({
              title: 'Success!',
              text: 'Vehicle has been successfully added to real stock.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });

            // Reload the listing to reflect changes
            this.loadVehicles(this.currentPage, this.searchTerm, this.filters);
          } else {
            // Show error message
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add vehicle to real stock.',
              icon: 'error',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        },
        error: (error) => {
          // Remove from loading set
          this.addingToStockIds.delete(vehicleId);

          console.error('Error adding vehicle to stock:', error);

          // Show error message
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding vehicle to real stock.',
            icon: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  isAddingToStock(vehicleId: string): boolean {
    return this.addingToStockIds.has(vehicleId);
  }

  openEditModal(vehicle: any): void {
    this._updateCarModalService.setSelectedCar(vehicle);
    const modalElement = document.getElementById('kt_modal_update_car');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onCarUpdated(): void {
    // Reload the vehicles list after successful update
    this.loadVehicles(this.currentPage, this.searchTerm, this.filters);
  }
}
